/**
 * React hooks for messaging functionality
 * Provides hooks for conversations, messages, WebSocket connection, and typing indicators
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useEffect, useRef, useCallback, useState } from 'react';
import { apiClient, handleApiError } from '../client';
import { getWebSocketService } from '../services/websocket';
import type {
  Conversation,
  Message,
  SendMessageRequest,
  ConversationsResponse,
  MessagesResponse,
  UnreadCountResponse,
  NewMessageEvent,
  MessageReadEvent,
  UserTypingEvent,
  UserStoppedTypingEvent,
  WebSocketEvent,
} from '../types/messaging';

// API endpoints
const ENDPOINTS = {
  conversations: '/messages/conversations',
  conversation: (id: number) => `/messages/conversations/${id}`,
  messages: (id: number) => `/messages/conversations/${id}/messages`,
  sendMessage: (id: number) => `/messages/conversations/${id}/messages`,
  markRead: (id: number) => `/messages/messages/${id}/read`,
  markAllRead: (id: number) => `/messages/conversations/${id}/read-all`,
  unreadCount: '/messages/unread-count',
};

/**
 * Hook to fetch all conversations for the current user
 */
export function useConversations(options?: UseQueryOptions<ConversationsResponse>) {
  return useQuery<ConversationsResponse>({
    queryKey: ['conversations'],
    queryFn: async () => {
      const response = await apiClient.get<ConversationsResponse>(ENDPOINTS.conversations);
      return response.data;
    },
    ...options,
  });
}

/**
 * Hook to fetch a specific conversation
 */
export function useConversation(conversationId: number, options?: UseQueryOptions<Conversation>) {
  return useQuery<Conversation>({
    queryKey: ['conversation', conversationId],
    queryFn: async () => {
      const response = await apiClient.get<Conversation>(ENDPOINTS.conversation(conversationId));
      return response.data;
    },
    enabled: conversationId > 0,
    ...options,
  });
}

/**
 * Hook to fetch messages for a conversation with pagination
 */
export function useMessages(
  conversationId: number,
  params?: { limit?: number; before?: number },
  options?: UseQueryOptions<MessagesResponse>
) {
  return useQuery<MessagesResponse>({
    queryKey: ['messages', conversationId, params],
    queryFn: async () => {
      const response = await apiClient.get<MessagesResponse>(ENDPOINTS.messages(conversationId), {
        params,
      });
      return response.data;
    },
    enabled: conversationId > 0,
    ...options,
  });
}

/**
 * Hook to send a message via REST API (fallback when WebSocket unavailable)
 */
export function useSendMessage(conversationId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SendMessageRequest) => {
      const response = await apiClient.post<Message>(ENDPOINTS.sendMessage(conversationId), data);
      return response.data;
    },
    onSuccess: (message) => {
      // Update messages cache
      queryClient.setQueryData<MessagesResponse>(
        ['messages', conversationId],
        (old) => {
          if (!old) return { messages: [message], total: 1, hasMore: false };
          return {
            ...old,
            messages: [message, ...old.messages],
            total: old.total + 1,
          };
        }
      );

      // Invalidate conversations to update last message
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: (error) => {
      console.error('Failed to send message:', handleApiError(error));
    },
  });
}

/**
 * Hook to mark a message as read
 */
export function useMarkMessageRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (messageId: number) => {
      await apiClient.put(ENDPOINTS.markRead(messageId));
    },
    onSuccess: () => {
      // Invalidate queries to refresh unread counts
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['unreadCount'] });
    },
  });
}

/**
 * Hook to mark all messages in a conversation as read
 */
export function useMarkConversationRead(conversationId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await apiClient.post(ENDPOINTS.markAllRead(conversationId));
    },
    onSuccess: () => {
      // Update messages cache to mark all as read
      queryClient.invalidateQueries({ queryKey: ['messages', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['unreadCount'] });
    },
  });
}

/**
 * Hook to get total unread message count
 */
export function useUnreadCount(options?: UseQueryOptions<number>) {
  return useQuery<number>({
    queryKey: ['unreadCount'],
    queryFn: async () => {
      const response = await apiClient.get<UnreadCountResponse>(ENDPOINTS.unreadCount);
      return response.data.unreadCount;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
    ...options,
  });
}

/**
 * Hook to manage WebSocket connection for real-time messaging
 */
export function useWebSocket() {
  const queryClient = useQueryClient();
  const wsRef = useRef(getWebSocketService());
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    const ws = wsRef.current;
    const token = localStorage.getItem('auth_token');

    if (!token) {
      console.warn('No auth token found, cannot connect to WebSocket');
      return;
    }

    // Connect to WebSocket
    ws.connect(token);

    // Handle connection events
    const handleConnected = () => {
      setIsConnected(true);
    };

    const handleError = () => {
      setIsConnected(false);
    };

    // Handle new messages
    const handleNewMessage = (event: WebSocketEvent) => {
      const messageEvent = event as NewMessageEvent;
      const { message } = messageEvent;

      // Update messages cache
      queryClient.setQueryData<MessagesResponse>(
        ['messages', message.conversationId],
        (old) => {
          if (!old) return { messages: [message], total: 1, hasMore: false };
          return {
            ...old,
            messages: [message, ...old.messages],
            total: old.total + 1,
          };
        }
      );

      // Invalidate conversations to update last message
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['unreadCount'] });
    };

    // Handle message read events
    const handleMessageRead = (event: WebSocketEvent) => {
      const readEvent = event as MessageReadEvent;

      // Update messages cache
      queryClient.setQueryData<MessagesResponse>(
        ['messages', readEvent.conversationId],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            messages: old.messages.map((msg) =>
              msg.id === readEvent.messageId ? { ...msg, readAt: new Date().toISOString() } : msg
            ),
          };
        }
      );

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['unreadCount'] });
    };

    // Register event handlers
    ws.on('connected', handleConnected);
    ws.on('error', handleError);
    ws.on('new_message', handleNewMessage);
    ws.on('message_read', handleMessageRead);
    ws.on('conversation_read', handleMessageRead);

    // Cleanup on unmount
    return () => {
      ws.off('connected', handleConnected);
      ws.off('error', handleError);
      ws.off('new_message', handleNewMessage);
      ws.off('message_read', handleMessageRead);
      ws.off('conversation_read', handleMessageRead);
      ws.disconnect();
    };
  }, [queryClient]);

  // Send message via WebSocket
  const sendMessage = useCallback(
    (conversationId: number, content: string) => {
      const ws = wsRef.current;
      if (ws.isConnected()) {
        ws.send({
          type: 'send_message',
          conversationId,
          content,
        });
      } else {
        console.warn('WebSocket not connected, message not sent');
      }
    },
    []
  );

  // Mark message as read via WebSocket
  const markAsRead = useCallback((messageId: number) => {
    const ws = wsRef.current;
    if (ws.isConnected()) {
      ws.send({
        type: 'mark_read',
        messageId,
      });
    }
  }, []);

  return {
    isConnected,
    sendMessage,
    markAsRead,
  };
}

/**
 * Hook to manage typing indicators
 */
export function useTypingIndicator(conversationId: number) {
  const wsRef = useRef(getWebSocketService());
  const [typingUsers, setTypingUsers] = useState<Set<string>>(new Set());
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const ws = wsRef.current;

    // Handle typing events
    const handleTyping = (event: WebSocketEvent) => {
      const typingEvent = event as UserTypingEvent;
      if (typingEvent.conversationId === conversationId) {
        setTypingUsers((prev) => new Set(prev).add(`${typingEvent.userType}:${typingEvent.userId}`));

        // Auto-remove after 5 seconds
        setTimeout(() => {
          setTypingUsers((prev) => {
            const next = new Set(prev);
            next.delete(`${typingEvent.userType}:${typingEvent.userId}`);
            return next;
          });
        }, 5000);
      }
    };

    const handleStopTyping = (event: WebSocketEvent) => {
      const stoppedEvent = event as UserStoppedTypingEvent;
      if (stoppedEvent.conversationId === conversationId) {
        setTypingUsers((prev) => {
          const next = new Set(prev);
          next.delete(`${stoppedEvent.userType}:${stoppedEvent.userId}`);
          return next;
        });
      }
    };

    ws.on('user_typing', handleTyping);
    ws.on('user_stopped_typing', handleStopTyping);

    return () => {
      ws.off('user_typing', handleTyping);
      ws.off('user_stopped_typing', handleStopTyping);
    };
  }, [conversationId]);

  // Send typing indicator
  const startTyping = useCallback(() => {
    const ws = wsRef.current;
    if (ws.isConnected()) {
      ws.send({
        type: 'typing',
        conversationId,
      });

      // Auto-stop typing after 3 seconds
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      typingTimeoutRef.current = setTimeout(() => {
        stopTyping();
      }, 3000);
    }
  }, [conversationId]);

  // Stop typing indicator
  const stopTyping = useCallback(() => {
    const ws = wsRef.current;
    if (ws.isConnected()) {
      ws.send({
        type: 'stop_typing',
        conversationId,
      });
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
  }, [conversationId]);

  return {
    typingUsers: Array.from(typingUsers),
    isTyping: typingUsers.size > 0,
    startTyping,
    stopTyping,
  };
}
